import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { SharedModule } from "../../../../shared/shared.module";
import { LignesFacadeService } from "../../../ligne/services/lignes/lignes-facade.service";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { AideContextComponent } from "../../../../shared/components/aide-context/aide-context.component";
import { AideContextSection } from "../../../../shared/models/aide-context.model";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { format } from "date-fns";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { OF } from "../../../of/models/of.model";

const MONTHS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Jun",
  "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc",
];

@Component({
  selector: "vl-widget-couts-ligne",
  standalone: true,
  imports: [CommonModule, WidgetTitleComponent, SharedModule, AideContextComponent, LoaderSkeletonComponent],
  templateUrl: "./widget-couts-ligne.component.html",
  styleUrls: ["./widget-couts-ligne.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetCoutsLigneComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  private ligneFacade = inject(LignesFacadeService);
  private elementRef = inject(ElementRef);
  stats = this.ligneFacade.getState().stats;

  aideSections: AideContextSection[] = [
    { type: "text", html: TranslateUtils.translate("stats_ligne.aide_couts") },
    { type: "highlight", title: "Calcul", html: TranslateUtils.translate("stats_ligne.aide_couts_formule") },
  ];

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("coutsChart");
  chart?: Chart;

  chartData = computed(() => {
    const ofs: OF[] = this.stats()?.ofs ?? [];
    if (!ofs.length) return { labels: [], matiere: [], personnel: [] };

    const grouped = new Map<
      string,
      { matiere: number; personnel: number }
    >();
    for (const of of ofs) {
      if (!of.dateFin) continue;
      const key = format(new Date(of.dateFin), "yyyy-MM");
      const entry = grouped.get(key) ?? { matiere: 0, personnel: 0 };
      entry.matiere += parseNumber(
        of.coutMatiereTotalReel ?? of.coutMatiereTotalTheorique ?? 0,
      );
      entry.personnel += parseNumber(
        of.coutPersonnelTotalReel ?? of.coutPersonnelTotalTheorique ?? 0,
      );
      grouped.set(key, entry);
    }

    const entries = [...grouped.entries()].sort((a, b) =>
      a[0].localeCompare(b[0]),
    );

    return {
      labels: entries.map(([k]) => this.formatLabel(k)),
      matiere: entries.map(([, v]) => Math.round(v.matiere * 100) / 100),
      personnel: entries.map(([, v]) => Math.round(v.personnel * 100) / 100),
    };
  });

  constructor() {
    effect(
      () => {
        const canvas = this.canvasRef();
        const data = this.chartData();
        if (!canvas || !data.labels.length) {
          this.destroyChart();
          return;
        }
        this.buildChart(data);
      },
      { allowSignalWrites: true },
    );
  }

  private formatLabel(key: string): string {
    const m = key.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${MONTHS_FR[parseInt(m[2], 10) - 1]} ${m[1].slice(2)}`;
    return key;
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart(data: {
    labels: string[];
    matiere: number[];
    personnel: number[];
  }) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const textColor =
      getComputedStyle(this.elementRef.nativeElement)
        .getPropertyValue("--color-on-background-alt")
        .trim() || "#888";

    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: TranslateUtils.translate("stats_ligne.label_matiere"),
            data: data.matiere,
            backgroundColor: "hsl(210 70% 55%)",
            borderRadius: 4,
            stack: "costs",
          },
          {
            label: TranslateUtils.translate("stats_ligne.label_personnel"),
            data: data.personnel,
            backgroundColor: "hsl(35 90% 55%)",
            borderRadius: 4,
            stack: "costs",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { color: textColor, boxWidth: 12, padding: 8, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ` ${ctx.dataset.label}: ${WidgetHelperService.formatNumeric(ctx.parsed.y)}€`,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: textColor, maxRotation: 45, font: { size: 10 } },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 10 },
              callback: (value) => WidgetHelperService.formatNumeric(+value) + "€",
            },
          },
        },
      },
    };
    this.chart = new Chart(ctx, config);
  }
}
